import axios from 'axios';

/**
 * Demo Mode Interceptor
 * When demo_mode is active in localStorage, intercepts ALL failed API calls
 * and returns realistic fake success responses. Zero errors during demos.
 */

const genId = () => Math.random().toString(36).slice(2, 10);

const DEMO_COVER_LETTER = `Dear Hiring Manager,

I am writing to express my strong interest in this position. With over 5 years of experience in software engineering at companies like Stripe, Airbnb, and Figma, I bring a proven track record of building scalable products that serve millions of users.

My expertise in React, TypeScript, Python, and cloud infrastructure aligns closely with your requirements. At Stripe, I led the payments dashboard rebuild serving 500K+ merchants and reduced API latency by 40%. At Airbnb, I built real-time search infrastructure handling 10M+ queries daily.

I am particularly drawn to your company's mission and the opportunity to contribute to a high-impact engineering team. I would welcome the chance to discuss how my skills and experience can help drive your product forward.

Best regards,
Sarah Chen`;

const FAKE_RESPONSES = {
  'applications/create': {
    id: () => `demo-app-${genId()}`,
    status: 'applied',
    job_title: 'Software Engineer',
    company: 'Tech Company',
    applied_at: () => new Date().toISOString(),
  },
  'ai/generate-cover-letter': {
    cover_letter: DEMO_COVER_LETTER,
    cached: false,
  },
  'ai/optimize-cv': {
    optimization_id: () => `demo-opt-${genId()}`,
    status: 'complete',
    versions: [
      { id: 'v1', label: 'Skills-Focused', strategy: 'Highlights technical skills', summary: 'Senior Software Engineer specializing in full-stack development...', skills: ['React', 'TypeScript', 'Python', 'AWS', 'Node.js'], match_score: 92, key_changes: ['Added relevant keywords', 'Reordered skills'] },
      { id: 'v2', label: 'Achievement-Focused', strategy: 'Emphasizes measurable results', summary: 'Results-driven engineer with track record of 40% latency improvements...', skills: ['System Design', 'Performance', 'Leadership', 'React', 'Python'], match_score: 88, key_changes: ['Quantified achievements', 'Added impact metrics'] }
    ],
    total_jobs: 1,
  },
  'swipes/use': {
    allowed: true,
    remaining: 99,
  },
  'swipes/status': {
    swipes_used: 1,
    plan_remaining: 99,
    total_remaining: 99,
    is_unlimited: true,
  },
  'applications/bulk-apply': {
    bulk_id: () => `demo-bulk-${genId()}`,
    status: 'processing',
    total_jobs: 5,
  },
  'cv/save-optimized': {
    id: () => `demo-cv-${genId()}`,
    filename: 'CV - Optimized (Demo)',
  },
  'applications/contact-recruiter': {
    status: 'sending',
    message: 'Pitch sent successfully',
  },
};

function resolveValue(val) {
  return typeof val === 'function' ? val() : val;
}

function resolveObject(obj) {
  const result = {};
  for (const [key, val] of Object.entries(obj)) {
    result[key] = resolveValue(val);
  }
  return result;
}

function getFakeResponse(url) {
  for (const [pattern, data] of Object.entries(FAKE_RESPONSES)) {
    if (url.includes(pattern)) {
      return resolveObject(data);
    }
  }
  return null;
}

let interceptorId = null;

export function installDemoInterceptor() {
  if (interceptorId !== null) return;

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (localStorage.getItem('demo_mode') !== 'true') {
        return Promise.reject(error);
      }

      const url = error?.config?.url || '';
      const fakeData = getFakeResponse(url);

      if (fakeData) {
        return Promise.resolve({ data: fakeData, status: 200, statusText: 'OK (Demo)', config: error.config, headers: {} });
      }

      // For any other error in demo mode, return empty success
      return Promise.resolve({ data: {}, status: 200, statusText: 'OK (Demo)', config: error.config, headers: {} });
    }
  );
}

export function clearDemoMode() {
  localStorage.removeItem('demo_mode');
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
  }
}
